import { createContext, useContext, useReducer, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import apiService, { isTokenGetterAvailable } from '../services/api';
import { useAuth } from '../auth/Auth0ProviderWithHistory';

// Initial state
const initialState = {
  currentSection: 0,
  isSubmitted: false,
  submissionId: null,

  // Application state
  applicationStatus: null, // 'draft', 'submitted', 'under_review', 'approved', 'rejected'
  referenceNumber: null,
  submissionDate: null,
  lastUpdated: null,
  isReadOnly: false,
  hasExistingApplication: false,

  // UI state
  isLoadingApplication: false,
  applicationError: null,

  sections: {
    introduction: {
      status: 'completed', // Introduction is always completed
      data: {},
      errors: {}
    },
    companyDetails: {
      status: 'not_started', // not_started, in_progress, completed
      data: {
        companyName: '',
        jurisdiction: '',
        registrationNumber: '',
        incorporationDate: '',
        registeredAddress: '',
        registeredCity: '',
        registeredPostcode: '',
        registeredCountry: '',
        isDifferentBusinessAddress: false,
        businessAddress: '',
        businessCity: '',
        businessPostcode: '',
        businessCountry: '',
        website: '',
        businessActivity: '',
        companyType: '',
        taxId: ''
      },
      errors: {}
    },
    contactInformation: {
      status: 'not_started',
      data: {
        contactName: '',
        position: '',
        email: '',
        phoneCountry: '',
        phoneNumber: '',
        address: '',
        city: '',
        postcode: '',
        country: ''
      },
      errors: {}
    },
    pepInformation: {
      status: 'not_started',
      data: {
        hasPep: null,
        pepPersons: []
      },
      errors: {}
    },
    sanctionsAudit: {
      status: 'not_started',
      data: {
        hasSanctions: null,
        sanctionContext: '',
        hasAudit: null,
        auditReport: null
      },
      errors: {}
    },
    businessPresentation: {
      status: 'not_started',
      data: {
        businessDescription: '',
        paymentFlows: '',
        geographicDistribution: '',
        customerTypes: '',
        cryptoExperience: '',
        riskAssessment: ''
      },
      errors: {}
    },
    documentation: {
      status: 'not_started',
      data: {
        incorporationDocs: null,
        memorandumArticles: null,
        certificateIncorporation: null,
        companyStructure: null,
        signatoryList: null,
        proofAddress: null,
        regulatoryLicenses: null,
        additionalDocs: null
      },
      errors: {}
    }
  }
};

// Action types
const actionTypes = {
  SET_CURRENT_SECTION: 'SET_CURRENT_SECTION',
  UPDATE_SECTION_DATA: 'UPDATE_SECTION_DATA',
  SET_SECTION_STATUS: 'SET_SECTION_STATUS',
  SET_SECTION_ERRORS: 'SET_SECTION_ERRORS',
  CLEAR_SECTION_ERRORS: 'CLEAR_SECTION_ERRORS',
  SUBMIT_FORM: 'SUBMIT_FORM',
  LOAD_SAVED_DATA: 'LOAD_SAVED_DATA',
  RESET_FORM: 'RESET_FORM',

  // Application state actions
  SET_APPLICATION_STATUS: 'SET_APPLICATION_STATUS',
  SET_LOADING_APPLICATION: 'SET_LOADING_APPLICATION',
  SET_APPLICATION_ERROR: 'SET_APPLICATION_ERROR',
  LOAD_EXISTING_APPLICATION: 'LOAD_EXISTING_APPLICATION',
  SET_READ_ONLY_MODE: 'SET_READ_ONLY_MODE'
};

// Reducer
function formReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_CURRENT_SECTION:
      return {
        ...state,
        currentSection: action.payload
      };

    case actionTypes.UPDATE_SECTION_DATA:
      return {
        ...state,
        sections: {
          ...state.sections,
          [action.payload.section]: {
            ...state.sections[action.payload.section],
            data: {
              ...state.sections[action.payload.section].data,
              ...action.payload.data
            },
            status: action.payload.status || state.sections[action.payload.section].status
          }
        }
      };

    case actionTypes.SET_SECTION_STATUS:
      return {
        ...state,
        sections: {
          ...state.sections,
          [action.payload.section]: {
            ...state.sections[action.payload.section],
            status: action.payload.status
          }
        }
      };

    case actionTypes.SET_SECTION_ERRORS:
      return {
        ...state,
        sections: {
          ...state.sections,
          [action.payload.section]: {
            ...state.sections[action.payload.section],
            errors: action.payload.errors
          }
        }
      };

    case actionTypes.CLEAR_SECTION_ERRORS:
      return {
        ...state,
        sections: {
          ...state.sections,
          [action.payload.section]: {
            ...state.sections[action.payload.section],
            errors: {}
          }
        }
      };

    case actionTypes.SUBMIT_FORM:
      return {
        ...state,
        isSubmitted: true,
        submissionId: action.payload.submissionId
      };

    case actionTypes.LOAD_SAVED_DATA:
      return {
        ...state,
        ...action.payload
      };

    case actionTypes.RESET_FORM:
      return initialState;

    case actionTypes.SET_APPLICATION_STATUS:
      return {
        ...state,
        applicationStatus: action.payload.status,
        referenceNumber: action.payload.referenceNumber,
        submissionDate: action.payload.submissionDate,
        lastUpdated: action.payload.lastUpdated,
        hasExistingApplication: action.payload.hasApplication,
        isReadOnly: action.payload.isReadOnly
      };

    case actionTypes.SET_LOADING_APPLICATION:
      return {
        ...state,
        isLoadingApplication: action.payload
      };

    case actionTypes.SET_APPLICATION_ERROR:
      return {
        ...state,
        applicationError: action.payload,
        isLoadingApplication: false
      };

    case actionTypes.LOAD_EXISTING_APPLICATION:
      return {
        ...state,
        sections: {
          ...state.sections,
          ...action.payload.sections
        },
        hasExistingApplication: true,
        isLoadingApplication: false
      };

    case actionTypes.SET_READ_ONLY_MODE:
      return {
        ...state,
        isReadOnly: action.payload
      };

    default:
      return state;
  }
}

// Context
const FormContext = createContext();

// Provider component
export function FormProvider({ children }) {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth();

  // Load existing application data only when user is authenticated
  useEffect(() => {
    // Don't load data if auth is still loading or user is not authenticated
    if (authLoading) {
      console.log('[FormContext] Auth still loading, waiting...');
      return;
    }

    if (!isAuthenticated) {
      console.log('[FormContext] User not authenticated, skipping data load');
      return;
    }

    if (!getAccessTokenSilently) {
      console.log('[FormContext] Token getter not available, waiting...');
      return;
    }

    // Double-check that the API service has the token getter
    if (!isTokenGetterAvailable()) {
      console.log('[FormContext] API service token getter not available, waiting...');
      return;
    }

    const loadApplicationData = async () => {
      try {
        dispatch({ type: actionTypes.SET_LOADING_APPLICATION, payload: true });

        // Check application status first
        const statusResult = await apiService.getApplicationStatus();

        if (statusResult.success && statusResult.data.hasApplication) {
          // Set application status
          dispatch({
            type: actionTypes.SET_APPLICATION_STATUS,
            payload: statusResult.data
          });

          // Load existing sections data if application exists
          const sectionsResult = await apiService.loadAllSectionsData();

          if (sectionsResult.success && sectionsResult.data.hasData) {
            dispatch({
              type: actionTypes.LOAD_EXISTING_APPLICATION,
              payload: sectionsResult.data
            });
            console.log('[FormContext] Loaded existing application data');
          } else {
            dispatch({ type: actionTypes.SET_LOADING_APPLICATION, payload: false });
          }
        } else {
          // No existing application, try to load from localStorage
          const savedData = localStorage.getItem('onboarding-form-data');
          if (savedData) {
            try {
              const parsedData = JSON.parse(savedData);
              dispatch({
                type: actionTypes.LOAD_SAVED_DATA,
                payload: parsedData
              });
              console.log('[FormContext] Loaded data from localStorage');
            } catch (error) {
              console.error('Error loading saved data from localStorage:', error);
            }
          }
          dispatch({ type: actionTypes.SET_LOADING_APPLICATION, payload: false });
        }
      } catch (error) {
        console.error('Error loading application data:', error);
        dispatch({
          type: actionTypes.SET_APPLICATION_ERROR,
          payload: 'Failed to load application data'
        });
      }
    };

    console.log('[FormContext] Authentication ready, loading application data...');
    loadApplicationData();
  }, [authLoading, isAuthenticated, getAccessTokenSilently]);

  // Save data to localStorage whenever state changes (only if not submitted and not read-only)
  useEffect(() => {
    if (!state.isSubmitted && !state.isReadOnly && !state.hasExistingApplication) {
      localStorage.setItem('onboarding-form-data', JSON.stringify(state));
    }
  }, [state]);

  // Action creators
  const actions = {
    setCurrentSection: (sectionIndex) => {
      dispatch({
        type: actionTypes.SET_CURRENT_SECTION,
        payload: sectionIndex
      });
    },

    updateSectionData: (section, data, status) => {
      dispatch({
        type: actionTypes.UPDATE_SECTION_DATA,
        payload: { section, data, status }
      });
    },

    setSectionStatus: (section, status) => {
      dispatch({
        type: actionTypes.SET_SECTION_STATUS,
        payload: { section, status }
      });
    },

    setSectionErrors: (section, errors) => {
      dispatch({
        type: actionTypes.SET_SECTION_ERRORS,
        payload: { section, errors }
      });
    },

    clearSectionErrors: (section) => {
      dispatch({
        type: actionTypes.CLEAR_SECTION_ERRORS,
        payload: { section }
      });
    },

    submitForm: () => {
      const submissionId = uuidv4();
      dispatch({
        type: actionTypes.SUBMIT_FORM,
        payload: { submissionId }
      });
      // Clear localStorage after submission
      localStorage.removeItem('onboarding-form-data');
    },

    resetForm: () => {
      dispatch({
        type: actionTypes.RESET_FORM
      });
      localStorage.removeItem('onboarding-form-data');
    },

    // Application state actions
    setApplicationStatus: (statusData) => {
      dispatch({
        type: actionTypes.SET_APPLICATION_STATUS,
        payload: statusData
      });
    },

    setLoadingApplication: (isLoading) => {
      dispatch({
        type: actionTypes.SET_LOADING_APPLICATION,
        payload: isLoading
      });
    },

    setApplicationError: (error) => {
      dispatch({
        type: actionTypes.SET_APPLICATION_ERROR,
        payload: error
      });
    },

    loadExistingApplication: (applicationData) => {
      dispatch({
        type: actionTypes.LOAD_EXISTING_APPLICATION,
        payload: applicationData
      });
    },

    setReadOnlyMode: (isReadOnly) => {
      dispatch({
        type: actionTypes.SET_READ_ONLY_MODE,
        payload: isReadOnly
      });
    },

    // Refresh application data
    refreshApplicationData: async () => {
      try {
        dispatch({ type: actionTypes.SET_LOADING_APPLICATION, payload: true });

        const statusResult = await apiService.getApplicationStatus();

        if (statusResult.success && statusResult.data.hasApplication) {
          dispatch({
            type: actionTypes.SET_APPLICATION_STATUS,
            payload: statusResult.data
          });

          const sectionsResult = await apiService.loadAllSectionsData();

          if (sectionsResult.success && sectionsResult.data.hasData) {
            dispatch({
              type: actionTypes.LOAD_EXISTING_APPLICATION,
              payload: sectionsResult.data
            });
          }
        }

        dispatch({ type: actionTypes.SET_LOADING_APPLICATION, payload: false });
      } catch (error) {
        console.error('Error refreshing application data:', error);
        dispatch({
          type: actionTypes.SET_APPLICATION_ERROR,
          payload: 'Failed to refresh application data'
        });
      }
    }
  };

  const value = {
    state,
    actions
  };

  return (
    <FormContext.Provider value={value}>
      {children}
    </FormContext.Provider>
  );
}

// Custom hook to use the form context
export function useForm() {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within a FormProvider');
  }
  return context;
}

// Section configuration
export const sectionConfig = [
  {
    id: 'introduction',
    title: 'Introduction',
    description: 'How to complete this application'
  },
  {
    id: 'companyDetails',
    title: 'Company Details',
    description: 'Basic information about your company'
  },
  {
    id: 'contactInformation',
    title: 'Contact Information',
    description: 'Primary contact details'
  },
  {
    id: 'pepInformation',
    title: 'PEP Information',
    description: 'Politically Exposed Persons declaration'
  },
  {
    id: 'sanctionsAudit',
    title: 'Sanctions & AML-FT',
    description: 'Sanctions and audit information'
  },
  {
    id: 'businessPresentation',
    title: 'Business Presentation',
    description: 'Detailed business information'
  },
  {
    id: 'documentation',
    title: 'Documentation',
    description: 'Required document uploads'
  }
];
